import logger from '../utils/logger';
/**
 * SCALEWAY STORAGE SERVICE - FRONTEND
 * 
 * Service frontend pour upload/download de fichiers vers Scaleway Object Storage
 * Utilise signed URLs pour upload direct (contourne le backend)
 */

import { getAuthHeaders } from '../utils/api-headers';
import { API_URL } from './config';

/**
 * Upload un fichier vers Scaleway Object Storage
 * @param file - Fichier à uploader
 * @param userId - ID utilisateur
 * @param sessionId - ID session (optionnel)
 * @param fileType - Type de fichier (upload, report, export)
 * @returns Promise<{fileKey: string, fileId: string}>
 */
export async function uploadFileToCloud(
 file: File,
 userId: string,
 sessionId?: string,
 fileType: 'upload' | 'report' | 'export' = 'upload'
): Promise<{ fileKey: string; fileId: string; orgPath: string }> {
 try {
 logger.log(` [Storage] Starting upload - File: ${file.name}, Size: ${file.size} bytes`);
 
 // Étape 1 : Obtenir URL signée depuis le backend
 // SÉCURITÉ: JWT automatique
 const authHeaders = await getAuthHeaders();
 const presignedResponse = await fetch(`${API_URL}/api/upload/presigned`, {
 method: 'POST',
 headers: {
 ...authHeaders,
 },
 body: JSON.stringify({
 userId,
 fileName: file.name,
 mimeType: file.type,
 fileSize: file.size,
 sessionId,
 fileType
 }),
 });
 
 if (!presignedResponse.ok) {
 const error = await presignedResponse.json();
 
 // Gérer cas tokens insuffisants
 if (error.error === 'insufficient_tokens') {
 throw new Error(`Tokens insuffisants : ${error.message || 'Rechargez votre compte'}`);
 }
 
 throw new Error(error.error || 'Échec obtention URL upload');
 }
 
 const { uploadUrl, fileKey, orgPath } = await presignedResponse.json();
 
 logger.log(` [Storage] Presigned URL obtained - Key: ${fileKey}`);
 
 // Étape 2 : Upload direct vers S3 (contourne le backend)
 const uploadResponse = await fetch(uploadUrl, {
 method: 'PUT',
 body: file,
 headers: {
 'Content-Type': file.type,
 },
 });
 
 if (!uploadResponse.ok) {
 logger.error(' [Storage] S3 upload failed:', uploadResponse.status, uploadResponse.statusText);
 throw new Error('Échec upload fichier vers le serveur');
 }
 
 logger.log(` [Storage] File uploaded to S3 - Key: ${fileKey}`);
 
 // Étape 3 : Confirmer upload et sauvegarder metadata
 // SÉCURITÉ: JWT automatique
 const confirmHeaders = await getAuthHeaders();
 const confirmResponse = await fetch(`${API_URL}/api/upload/confirm`, {
 method: 'POST',
 headers: {
 ...confirmHeaders,
 },
 body: JSON.stringify({
 userId,
 fileKey,
 fileName: file.name,
 mimeType: file.type,
 fileSize: file.size,
 sessionId,
 }),
 });
 
 if (!confirmResponse.ok) {
 const error = await confirmResponse.json();
 throw new Error(error.error || 'Échec confirmation upload');
 }
 
 const { file: fileRecord } = await confirmResponse.json();
 
 logger.log(` [Storage] Upload confirmed - File ID: ${fileRecord.id}`);
 
 return {
 fileKey,
 fileId: fileRecord.id,
 orgPath
 };
 
 } catch (error) {
 logger.error(' [Storage] Upload error:', error);
 throw error;
 }
}

/**
 * Upload un fichier avec processing (CSV, JSON, Excel)
 * @param file - Fichier à uploader
 * @param userId - ID utilisateur
 * @param sessionId - ID session (optionnel)
 * @returns Promise<{fileKey: string, fileId: string, dataPreview: any}>
 */
export async function uploadFileWithProcessing(
 file: File,
 userId: string,
 sessionId?: string
): Promise<{ fileKey: string; fileId: string; orgPath: string; dataPreview?: any }> {
 try {
 logger.log(` [Storage] Starting upload with processing - File: ${file.name}`);
 
 // Process file data avant upload
 let dataPreview = null;
 let rowCount = 0;
 let columnCount = 0;
 
 if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
 const text = await file.text();
 const lines = text.split('\n').filter(line => line.trim());
 rowCount = lines.length - 1; // Exclude header
 columnCount = lines[0]?.split(',').length || 0;
 
 // Store first 10 rows as preview
 dataPreview = lines.slice(0, Math.min(10, lines.length));
 } else if (file.type === 'application/json' || file.name.endsWith('.json')) {
 const text = await file.text();
 try {
 const jsonData = JSON.parse(text);
 if (Array.isArray(jsonData)) {
 rowCount = jsonData.length;
 columnCount = jsonData.length > 0 ? Object.keys(jsonData[0]).length : 0;
 dataPreview = jsonData.slice(0, Math.min(5, jsonData.length));
 }
 } catch (e) {
 logger.warn('Failed to parse JSON file:', e);
 }
 }
 
 // Étape 1 : Obtenir URL signée
 // SÉCURITÉ: JWT automatique
 const authHeaders2 = await getAuthHeaders();
 const presignedResponse = await fetch(`${API_URL}/api/upload/presigned`, {
 method: 'POST',
 headers: {
 ...authHeaders2,
 },
 body: JSON.stringify({
 userId,
 fileName: file.name,
 mimeType: file.type,
 fileSize: file.size,
 sessionId,
 fileType: 'upload'
 }),
 });
 
 if (!presignedResponse.ok) {
 const error = await presignedResponse.json();
 if (error.error === 'insufficient_tokens') {
 throw new Error(`Tokens insuffisants : ${error.message || 'Rechargez votre compte'}`);
 }
 throw new Error(error.error || 'Échec obtention URL upload');
 }
 
 const { uploadUrl, fileKey, orgPath } = await presignedResponse.json();
 
 // Étape 2 : Upload vers S3
 const uploadResponse = await fetch(uploadUrl, {
 method: 'PUT',
 body: file,
 headers: {
 'Content-Type': file.type,
 },
 });
 
 if (!uploadResponse.ok) {
 throw new Error('Échec upload fichier vers le serveur');
 }
 
 // Étape 3 : Confirmer avec data preview
 // SÉCURITÉ: JWT automatique
 const confirmHeaders2 = await getAuthHeaders();
 const confirmResponse = await fetch(`${API_URL}/api/upload/confirm`, {
 method: 'POST',
 headers: {
 ...confirmHeaders2,
 },
 body: JSON.stringify({
 userId,
 fileKey,
 fileName: file.name,
 mimeType: file.type,
 fileSize: file.size,
 sessionId,
 dataPreview,
 rowCount,
 columnCount
 }),
 });
 
 if (!confirmResponse.ok) {
 const error = await confirmResponse.json();
 throw new Error(error.error || 'Échec confirmation upload');
 }
 
 const { file: fileRecord } = await confirmResponse.json();
 
 logger.log(` [Storage] Upload with processing confirmed - File ID: ${fileRecord.id}`);
 
 return {
 fileKey,
 fileId: fileRecord.id,
 orgPath,
 dataPreview
 };
 
 } catch (error) {
 logger.error(' [Storage] Upload with processing error:', error);
 throw error;
 }
}

/**
 * Obtenir URL de téléchargement pour un fichier
 * @param fileId - ID du fichier
 * @param userId - ID utilisateur
 * @returns Promise<string> - URL de téléchargement
 */
export async function getFileDownloadUrl(fileId: string, userId: string): Promise<string> {
 try {
 logger.log(` [Storage] Getting download URL - File ID: ${fileId}`);
 
 // SÉCURITÉ: JWT automatique
 const authHeaders3 = await getAuthHeaders();
 const response = await fetch(`${API_URL}/api/files/${fileId}/download?userId=${userId}`, {
 method: 'GET',
 headers: {
 ...authHeaders3,
 },
 });
 
 if (!response.ok) {
 const error = await response.json();
 throw new Error(error.error || 'Échec obtention URL téléchargement');
 }
 
 const { downloadUrl } = await response.json();
 
 logger.log(` [Storage] Download URL obtained - File ID: ${fileId}`);
 
 return downloadUrl;
 
 } catch (error) {
 logger.error(' [Storage] Download URL error:', error);
 throw error;
 }
}

/**
 * Supprimer un fichier
 * @param fileId - ID du fichier
 * @param userId - ID utilisateur
 * @returns Promise<void>
 */
export async function deleteFile(fileId: string, userId: string): Promise<void> {
 try {
 logger.log(` [Storage] Deleting file - File ID: ${fileId}`);
 
 // SÉCURITÉ: JWT automatique
 const authHeaders4 = await getAuthHeaders();
 const response = await fetch(`${API_URL}/api/files/${fileId}`, {
 method: 'DELETE',
 headers: {
 ...authHeaders4,
 },
 body: JSON.stringify({ userId }),
 });
 
 if (!response.ok) {
 const error = await response.json();
 throw new Error(error.error || 'Échec suppression fichier');
 }
 
 logger.log(` [Storage] File deleted - File ID: ${fileId}`);
 
 } catch (error) {
 logger.error(' [Storage] Delete error:', error);
 throw error;
 }
}

/**
 * Télécharger un fichier (déclenche le download)
 * @param fileId - ID du fichier
 * @param userId - ID utilisateur
 * @param fileName - Nom du fichier (optionnel, pour le download)
 */
export async function downloadFile(fileId: string, userId: string, fileName?: string): Promise<void> {
 try {
 const downloadUrl = await getFileDownloadUrl(fileId, userId);
 
 // Créer un lien temporaire et trigger download
 const link = document.createElement('a');
 link.href = downloadUrl;
 link.download = fileName || 'download';
 link.click();
 
 } catch (error) {
 logger.error(' [Storage] Download error:', error);
 throw error;
 }
}

// ============================================================================
// DEEP REPORTS PDF UPLOAD
// ============================================================================

/**
 * Upload Deep Report PDF to Scaleway S3
 * @param pdfBlob - The PDF file as Blob
 * @param userId - User ID for multi-tenant organization
 * @param reportId - Deep Report ID from database
 * @param reportTitle - Title for filename
 * @returns fileKey in Scaleway S3 and download URL
 */
export async function uploadDeepReportPDF(
 pdfBlob: Blob,
 userId: string,
 reportId: string,
 reportTitle: string
): Promise<{ fileKey: string; downloadUrl: string }> {
 try {
 logger.log(` [Storage] Uploading Deep Report PDF to Scaleway - Size: ${pdfBlob.size} bytes`);
 
 // Sanitize title for filename
 const sanitizedTitle = reportTitle
 .replace(/[^a-zA-Z0-9\s-]/g, '')
 .replace(/\s+/g, '_')
 .substring(0, 50);
 
 const fileName = `Deep_Report_${sanitizedTitle}_${reportId.substring(0, 8)}.pdf`;
 const mimeType = 'application/pdf';
 const fileSize = pdfBlob.size;
 
 // Step 1: Get presigned upload URL
 // SÉCURITÉ: JWT automatique
 const authHeaders5 = await getAuthHeaders();
 const presignedResponse = await fetch(`${API_URL}/api/upload/presigned`, {
 method: 'POST',
 headers: { 
 ...authHeaders5
 },
 body: JSON.stringify({
 userId,
 fileName,
 mimeType,
 fileSize,
 fileType: 'deep_report_pdf'
 })
 });
 
 if (!presignedResponse.ok) {
 throw new Error('Échec génération URL de téléchargement PDF');
 }
 
 const { uploadUrl, fileKey } = await presignedResponse.json();
 logger.log(` [Storage] Uploading PDF to Scaleway - Key: ${fileKey}`);
 
 // Step 2: Upload PDF directly to Scaleway S3
 const uploadResponse = await fetch(uploadUrl, {
 method: 'PUT',
 headers: {
 'Content-Type': mimeType
 },
 body: pdfBlob
 });
 
 if (!uploadResponse.ok) {
 throw new Error(`Échec upload PDF: ${uploadResponse.status} ${uploadResponse.statusText}`);
 }
 
 logger.log(` [Storage] Deep Report PDF uploaded successfully - Key: ${fileKey}`);
 
 // Return fileKey as downloadUrl (will be converted to presigned URL by backend when needed)
 return { 
 fileKey, 
 downloadUrl: fileKey // Backend will generate presigned URL when downloading
 };
 
 } catch (error) {
 logger.error(' [Storage] Deep Report PDF upload error:', error);
 throw error;
 }
}

